import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import ReactDOM from 'react-dom/client';
import { setupApiClient } from '@api/api-client-config';
import { captureAttributionFromUrl } from '@api/attribution';
import { AuthProvider } from './contexts/auth-context';
import { GameProvider } from './contexts/game-context';
import { initializePrivacyMessaging } from './lib/privacy-messaging';
import { routeTree } from './routeTree.gen';

import '@fq/ui/index.css';

setupApiClient();

// Capture campaign params (utm_* + ref) from the landing URL before anything
// else — the Google OAuth popup round-trip can drop the query string later.
captureAttributionFromUrl();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultGcTime: 0,
  defaultPreloadStaleTime: 0,
});

router.subscribe('onResolved', initializePrivacyMessaging);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

function App() {
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <GameProvider>
            <RouterProvider router={router} context={{ queryClient }} />
          </GameProvider>
        </AuthProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

const rootElement = document.getElementById('root')!;
ReactDOM.createRoot(rootElement).render(<App />);
